/**
 * SlicePlane - Represents a 2D drawing plane in 3D space
 *
 * SlicePlanes are the fundamental building blocks of a chair design.
 * Each SlicePlane contains:
 * - A Sketch (2D drawing)
 * - A Plane (3D position and orientation)
 * - Metadata (thickness, selection state, etc.)
 *
 * Ported from cc.sketchchair.geometry.SlicePlane.java
 * Simplified for Phase 3 - focuses on core geometry and transforms
 */

import * as THREE from 'three';
import { Vec2D } from '../sketch/geometry/Vec2D.js';
import { SliceSlots } from './SliceSlots.js';
import { sampleRing } from './PolygonOps.js';

// Debug visualization flag (controlled by VITE_DEBUG_INTERSECTIONS env variable)
const DEBUG_INTERSECTIONS = (import.meta.env?.VITE_DEBUG_INTERSECTIONS ?? globalThis.VITE_DEBUG_INTERSECTIONS) === 'true';

export class SlicePlane {
    /**
     * Global plane ID counter
     */
    static planeIdCounter = 0;

    /**
     * Create a new SlicePlane
     * @param {Object} options - Configuration options
     */
    constructor(options = {}) {
        const {
            id = SlicePlane.planeIdCounter++,
            position = new THREE.Vector3(0, 0, 0),
            normal = new THREE.Vector3(0, 0, 1),
            sketch = null,
            thickness = 5.0, // Default material thickness in mm
            guide = false,
            selected = false
        } = options;

        // Core properties
        this.id = id;
        this.sketch = sketch;
        this.selected = selected;
        this.guide = guide; // Guide planes for alignment
        this.destroy = false; // Mark for deletion

        // Geometry
        this.plane = new THREE.Plane();
        this.plane.setFromNormalAndCoplanarPoint(normal.clone().normalize(), position);
        this.position = position.clone();
        this.normal = normal.clone().normalize();

        // Material properties
        this.thickness = thickness;

        // Profile bounds (calculated from sketch)
        this.profileHeight = 0;
        this.profileMinY = 0;
        this.profileMaxY = 0;

        // Slots and cross-slice data
        this.slots = new SliceSlots(); // Collection of slots on this plane
        this.coverPath = null; // Outer boundary path
        this.constraintSlot = null; // Slot this plane is constrained to
        this.crossSliceSelection = null; // Parent CrossSliceSelection that generated this plane
        this.tiedToLeg = false;

        // Profile frame for cross-slice planes (set by buildCrossSliceProfile).
        // Cross-slice profiles are built in a custom 2D frame (X along the
        // intersection axis, Y along world Z) that does NOT match the
        // getWorldPos/getPlanePos quaternion frame. When profileXAxis is set,
        // getProfilePos/getProfileWorldPos must be used for anything that has
        // to line up with the plane's profile geometry (e.g. slots).
        this.profileXAxis = null; // THREE.Vector3 or null

        // Debug (for development)
        this.debugPoints = [];
    }

    /**
     * Set the sketch for this plane
     * @param {Sketch} sketch - Sketch instance
     */
    setSketch(sketch) {
        this.sketch = sketch;
        if (sketch) {
            sketch.setOnSlicePlane(this);
        }
    }

    /**
     * Get the sketch
     * @returns {Sketch} The sketch
     */
    getSketch() {
        return this.sketch;
    }

    /**
     * Convert 2D sketch coordinates to 3D world coordinates
     * Applies the plane's transform to a 2D point
     * @param {Vec2D} vec2D - 2D point in sketch space (canvas pixels)
     * @returns {THREE.Vector3} 3D point in world space
     */
    getWorldPos(vec2D) {
        if (!vec2D) return null;

        // Scale from canvas pixels to world units (1 pixel = 0.01 units)
        const SCALE_FACTOR = 0.01;

        // Start with 2D point in local plane space (XY plane, Z=0)
        // Apply scaling from canvas pixels to world units
        const local = new THREE.Vector3(vec2D.x * SCALE_FACTOR, vec2D.y * SCALE_FACTOR, 0);

        // Create transform matrix for this plane
        // We need to rotate from Z-up (local) to the plane's normal direction
        const transform = this.getTransformMatrix();
        local.applyMatrix4(transform);

        return local;
    }

    /**
     * Convert 3D world coordinates to 2D sketch coordinates
     * Inverse of getWorldPos
     * @param {THREE.Vector3} vec3D - 3D point in world space
     * @returns {Vec2D} 2D point in sketch space (canvas pixels)
     */
    getPlanePos(vec3D) {
        if (!vec3D) return null;

        // Scale inverse: world units to canvas pixels
        const SCALE_FACTOR = 0.01;

        // Get inverse transform
        const transform = this.getTransformMatrix();
        const inverse = new THREE.Matrix4().copy(transform).invert();

        // Apply inverse transform
        const local = vec3D.clone().applyMatrix4(inverse);

        // Scale back from world units to canvas pixels (inverse of getWorldPos)
        return new Vec2D(local.x / SCALE_FACTOR, local.y / SCALE_FACTOR);
    }

    /**
     * Convert a 3D world position to this plane's PROFILE frame (sketch pixels).
     * For cross-slice planes the profile frame (X = profileXAxis, Y = world Z)
     * differs from the getPlanePos quaternion frame; for base planes they match.
     * @param {THREE.Vector3} vec3D - World position
     * @returns {Vec2D} 2D point in the same space as this plane's profile geometry
     */
    getProfilePos(vec3D) {
        if (!vec3D) return null;

        if (this.profileXAxis) {
            const SCALE_FACTOR = 0.01;
            const rel = new THREE.Vector3().subVectors(vec3D, this.position);
            return new Vec2D(
                rel.dot(this.profileXAxis) / SCALE_FACTOR,
                rel.z / SCALE_FACTOR
            );
        }

        return this.getPlanePos(vec3D);
    }

    /**
     * Convert a 2D profile-frame point (sketch pixels) to a 3D world position.
     * Inverse of getProfilePos.
     * @param {Vec2D} vec2D - 2D point in profile space
     * @returns {THREE.Vector3} World position
     */
    getProfileWorldPos(vec2D) {
        if (!vec2D) return null;

        if (this.profileXAxis) {
            const SCALE_FACTOR = 0.01;
            return this.position.clone()
                .addScaledVector(this.profileXAxis, vec2D.x * SCALE_FACTOR)
                .add(new THREE.Vector3(0, 0, vec2D.y * SCALE_FACTOR));
        }

        return this.getWorldPos(vec2D);
    }

    /**
     * Get transform matrix for this plane
     * Transforms from local XY plane to world space
     * @returns {THREE.Matrix4} Transform matrix
     */
    getTransformMatrix() {
        const matrix = new THREE.Matrix4();

        // Build rotation matrix to align Z axis with plane normal
        const up = new THREE.Vector3(0, 1, 0);
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), this.normal);

        // Combine rotation and translation
        matrix.compose(
            this.position,
            quaternion,
            new THREE.Vector3(1, 1, 1)
        );

        return matrix;
    }

    /**
     * Apply rotation matrix for rendering
     * Used by Three.js for positioning objects on this plane
     * @param {THREE.Object3D} object - Object to transform
     */
    applyTransform(object) {
        const transform = this.getTransformMatrix();
        object.matrix.copy(transform);
        object.matrixAutoUpdate = false;
    }

    /**
     * Get the plane's normal in the XY direction
     * @returns {number} Angle in radians
     */
    getPlaneAngle() {
        return Math.atan2(this.normal.y, this.normal.x);
    }

    /**
     * Check if a 3D point is on the positive side of this plane
     * @param {THREE.Vector3} point - Point to test
     * @returns {number} Positive if on front side, negative if on back side, 0 if on plane
     */
    classifyPoint(point) {
        return this.plane.distanceToPoint(point);
    }

    /**
     * Get intersection ray between this plane and another plane
     * @param {SlicePlane} otherPlane - Other slice plane
     * @returns {THREE.Line3|null} Intersection line, or null if parallel
     */
    getIntersectionLine(otherPlane) {
        // Cross product of normals gives direction of intersection line
        const direction = new THREE.Vector3()
            .crossVectors(this.normal, otherPlane.normal);

        // If parallel (cross product is zero), no intersection
        if (direction.lengthSq() < 0.0001) {
            return null;
        }

        direction.normalize();

        if (DEBUG_INTERSECTIONS) {
            console.log(`🐛 getIntersectionLine:`);
            console.log(`   → This plane normal: (${this.normal.x.toFixed(3)}, ${this.normal.y.toFixed(3)}, ${this.normal.z.toFixed(3)})`);
            console.log(`   → Other plane normal: (${otherPlane.normal.x.toFixed(3)}, ${otherPlane.normal.y.toFixed(3)}, ${otherPlane.normal.z.toFixed(3)})`);
            console.log(`   → Intersection direction: (${direction.x.toFixed(3)}, ${direction.y.toFixed(3)}, ${direction.z.toFixed(3)})`);
        }

        // Find a point on the intersection line
        // Solve system of equations for the two planes
        // Using method from: https://mathworld.wolfram.com/Plane-PlaneIntersection.html

        const d1 = -this.plane.constant;
        const d2 = -otherPlane.plane.constant;

        const n1 = this.normal;
        const n2 = otherPlane.normal;

        const n1CrossN2 = direction;
        const denom = n1CrossN2.lengthSq();

        const point = new THREE.Vector3();
        point.addScaledVector(
            new THREE.Vector3().crossVectors(n2, n1CrossN2),
            d1 / denom
        );
        point.addScaledVector(
            new THREE.Vector3().crossVectors(n1CrossN2, n1),
            d2 / denom
        );

        // Create line (we'll extend it far in both directions)
        const far = 10000;
        return new THREE.Line3(
            point.clone().addScaledVector(direction, -far),
            point.clone().addScaledVector(direction, far)
        );
    }

    /**
     * Get intersection with another plane (complete implementation)
     *
     * Calculates where this plane's sketch intersects with another plane,
     * returning both 3D world coordinates and 2D local coordinates for
     * slot positioning.
     *
     * Based on SlicePlane.java getIntersectionWith() lines 400-500
     *
     * @param {SlicePlane} otherPlane - Other slice plane
     * @returns {Array<Object>} Array of intersection data:
     *   [{world: Vec3D, local: Vec2D, direction: Vec2D, edgeIndex: number}]
     */
    getIntersectionWith(otherPlane, onlyShapeIndex = null) {
        const intersections = [];

        // Get the 3D intersection line between planes
        const intersectionLine = this.getIntersectionLine(otherPlane);
        if (!intersectionLine) {
            return intersections; // Planes are parallel
        }

        // Check if we have a sketch to intersect
        if (!this.sketch || !this.sketch.closedShapes || this.sketch.closedShapes.length === 0) {
            if (DEBUG_INTERSECTIONS) {
                console.log(`🐛 getIntersectionWith: No sketch data (sketch: ${!!this.sketch}, closedShapes: ${this.sketch?.closedShapes?.length || 0})`);
            }
            return intersections;
        }

        if (DEBUG_INTERSECTIONS) {
            console.log(`🐛 getIntersectionWith: Checking ${this.sketch.closedShapes.length} shapes at plane Z=${this.position.z.toFixed(3)}`);
        }

        // For each shape on this plane. When onlyShapeIndex is set
        // ("through all" OFF - the default), only the shape that generated
        // the slice group is intersected.
        this.sketch.closedShapes.forEach((shape, shapeIdx) => {
            if (onlyShapeIndex !== null && shapeIdx !== onlyShapeIndex) {
                return;
            }
            const path = shape.path || shape;

            // Skip if no points
            if (!path.points || path.points.length < 2) {
                return;
            }

            // BEZIER-SAMPLE the outline before the edge walk (same sampler
            // as meshes/exports): chording across bezier control points
            // made ribs on a beziered profile miss the true curve and come
            // out longer than the profile. Straight outlines pass through.
            const walkPts = path.points.some(pt => pt.containsBezier && pt.containsBezier())
                ? sampleRing(path.points)
                : path.points;

            // Check each edge of the (sampled) path
            for (let i = 0; i < walkPts.length; i++) {
                const p1_2D = walkPts[i];
                const p2_2D = walkPts[(i + 1) % walkPts.length];

                // Convert to 3D world coordinates
                const p1_3D = this.getWorldPos(p1_2D);
                const p2_3D = this.getWorldPos(p2_2D);

                // Classify points relative to other plane
                const side1 = otherPlane.classifyPoint(p1_3D);
                const side2 = otherPlane.classifyPoint(p2_3D);

                // If points are on opposite sides, the edge crosses the
                // plane. A point EXACTLY on the plane (side 0 - e.g. a
                // bezier sample landing on the rib plane) counts once, via
                // the edge leaving it, or a strict sign change would drop
                // the crossing entirely.
                if ((side1 > 0 && side2 < 0) || (side1 < 0 && side2 > 0) ||
                    (side1 === 0 && side2 !== 0)) {
                    const intersection = this.calculateEdgeIntersection(
                        p1_3D, p2_3D, p1_2D, p2_2D,
                        otherPlane, intersectionLine, i
                    );
                    if (intersection) {
                        intersections.push(intersection);
                    }
                }
            }
        });

        return intersections;
    }

    /**
     * Calculate intersection between an edge and another plane
     * @private
     */
    calculateEdgeIntersection(p1_3D, p2_3D, p1_2D, p2_2D, otherPlane, intersectionLine, edgeIndex) {
        // Create ray along the edge
        const edgeDir = new THREE.Vector3().subVectors(p2_3D, p1_3D);
        const edgeLength = edgeDir.length();
        edgeDir.normalize();

        // Find intersection with other plane using parametric ray equation
        const ray = new THREE.Ray(p1_3D, edgeDir);
        const intersectPoint = new THREE.Vector3();

        // Intersect ray with other plane
        const result = ray.intersectPlane(otherPlane.plane, intersectPoint);

        if (!result) {
            return null; // No intersection
        }

        // Check if intersection is within edge segment
        const dist = p1_3D.distanceTo(intersectPoint);
        if (dist > edgeLength + 0.001) {
            return null; // Beyond edge
        }

        // Convert back to local 2D coordinates
        const local2D = this.getPlanePos(intersectPoint);
        if (!local2D) {
            return null;
        }

        // Calculate direction (perpendicular to intersection line in this plane)
        const lineDir3D = new THREE.Vector3()
            .subVectors(intersectionLine.end, intersectionLine.start)
            .normalize();

        // Project line direction onto this plane and convert to 2D
        const endPoint = intersectPoint.clone().addScaledVector(lineDir3D, 1);
        const endPoint2D = this.getPlanePos(endPoint);

        let direction2D = null;
        if (endPoint2D) {
            direction2D = new Vec2D(
                endPoint2D.x - local2D.x,
                endPoint2D.y - local2D.y
            ).normalize();
        } else {
            direction2D = new Vec2D(1, 0); // Fallback
        }

        return {
            world: intersectPoint.clone(),
            local: local2D,
            direction: direction2D,
            edgeIndex: edgeIndex
        };
    }

    /**
     * Get intersection with another plane using 2D approach
     * (Simpler than full 3D plane-plane intersection)
     *
     * This projects the otherPlane onto this plane's 2D coordinate system
     * and finds where a line across this plane's sketch intersects the edges.
     *
     * Works best when this plane is horizontal (primary sketch plane) and
     * otherPlane is a vertical cross-slice.
     *
     * @param {SlicePlane} otherPlane - Cross-slice plane
     * @returns {Array<Object>} Array of {world: Vec3, local: Vec2D, direction: Vec2D}
     */
    getIntersectionWith2D(otherPlane) {
        const intersections = [];

        // Check if we have a sketch to intersect
        if (!this.sketch || !this.sketch.closedShapes || this.sketch.closedShapes.length === 0) {
            return intersections;
        }

        // Project otherPlane's position onto this plane's 2D coordinate system
        const crossSlicePos2D = this.getPlanePos(otherPlane.position);
        if (!crossSlicePos2D) {
            return intersections;
        }

        // Project otherPlane's normal onto this plane to get 2D line direction
        // The normal tells us which way the cross-slice is facing
        // We need a point "forward" from the position to get the direction
        const forwardPoint3D = otherPlane.position.clone().addScaledVector(otherPlane.normal, 1);
        const forwardPoint2D = this.getPlanePos(forwardPoint3D);

        if (!forwardPoint2D) {
            return intersections;
        }

        // Calculate 2D direction of the cross-slice line on this plane
        // Use the projected normal direction directly (matches yellow line)
        const direction2D = new Vec2D(
            forwardPoint2D.x - crossSlicePos2D.x,
            forwardPoint2D.y - crossSlicePos2D.y
        ).normalize();

        // 🐛 REVERTED: Use normal direction directly, no rotation
        // This matches the yellow debug line (normal direction)

        // Create a very long 2D line segment (extend in both directions)
        const lineLength = 10000; // Large enough to cross entire sketch
        const lineStart2D = new Vec2D(
            crossSlicePos2D.x - direction2D.x * lineLength,
            crossSlicePos2D.y - direction2D.y * lineLength
        );
        const lineEnd2D = new Vec2D(
            crossSlicePos2D.x + direction2D.x * lineLength,
            crossSlicePos2D.y + direction2D.y * lineLength
        );

        if (DEBUG_INTERSECTIONS) {
            console.log(`🐛 2D INTERSECTION:`);
            console.log(`   → Cross-slice 3D position: (${otherPlane.position.x.toFixed(3)}, ${otherPlane.position.y.toFixed(3)}, ${otherPlane.position.z.toFixed(3)})`);
            console.log(`   → Cross-slice 2D position: (${crossSlicePos2D.x.toFixed(2)}, ${crossSlicePos2D.y.toFixed(2)})`);
            console.log(`   → 2D line direction: (${direction2D.x.toFixed(3)}, ${direction2D.y.toFixed(3)})`);
            console.log(`   → 2D line: (${lineStart2D.x.toFixed(1)}, ${lineStart2D.y.toFixed(1)}) → (${lineEnd2D.x.toFixed(1)}, ${lineEnd2D.y.toFixed(1)})`);
        }

        // For each shape on this plane
        this.sketch.closedShapes.forEach(shape => {
            const path = shape.path || shape;

            // Skip if no points
            if (!path.points || path.points.length < 2) {
                return;
            }

            if (DEBUG_INTERSECTIONS) {
                console.log(`   → Checking ${path.points.length} edges...`);
            }

            // Check each edge of the path (bezier-sampled - see
            // getIntersectionWith for why chording breaks beziered profiles)
            const walkPts = path.points.some(pt => pt.containsBezier && pt.containsBezier())
                ? sampleRing(path.points)
                : path.points;
            for (let i = 0; i < walkPts.length; i++) {
                const p1_2D = walkPts[i];
                const p2_2D = walkPts[(i + 1) % walkPts.length];

                // Find 2D line-line intersection
                const intersection2D = this.lineIntersection2D(
                    lineStart2D, lineEnd2D,
                    p1_2D, p2_2D
                );

                if (intersection2D) {
                    // Convert back to 3D world coordinates
                    const world3D = this.getWorldPos(intersection2D);

                    if (DEBUG_INTERSECTIONS) {
                        console.log(`   ✓ 2D INTERSECTION at edge ${i}:`);
                        console.log(`      - 2D intersection: (${intersection2D.x.toFixed(2)}, ${intersection2D.y.toFixed(2)})`);
                        console.log(`      - Edge p1: (${p1_2D.x.toFixed(2)}, ${p1_2D.y.toFixed(2)})`);
                        console.log(`      - Edge p2: (${p2_2D.x.toFixed(2)}, ${p2_2D.y.toFixed(2)})`);
                        console.log(`      - Converted to world: (${world3D.x.toFixed(3)}, ${world3D.y.toFixed(3)}, ${world3D.z.toFixed(3)})`);
                    }

                    intersections.push({
                        world: world3D,
                        local: intersection2D,
                        direction: direction2D,
                        edgeIndex: i
                    });
                }
            }
        });

        if (DEBUG_INTERSECTIONS) {
            console.log(`   → Found ${intersections.length} intersections`);
        }
        return intersections;
    }

    /**
     * Calculate 2D line-line intersection
     * @param {Vec2D} l1p1 - Line 1 start
     * @param {Vec2D} l1p2 - Line 1 end
     * @param {Vec2D} l2p1 - Line 2 start
     * @param {Vec2D} l2p2 - Line 2 end
     * @returns {Vec2D|null} Intersection point or null
     */
    lineIntersection2D(l1p1, l1p2, l2p1, l2p2) {
        const x1 = l1p1.x, y1 = l1p1.y;
        const x2 = l1p2.x, y2 = l1p2.y;
        const x3 = l2p1.x, y3 = l2p1.y;
        const x4 = l2p2.x, y4 = l2p2.y;

        const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

        // Lines are parallel
        if (Math.abs(denom) < 0.0001) {
            return null;
        }

        const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
        const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

        // Check if intersection is within line segments
        // For the cross-slice line (l1), we don't need to check bounds (it's infinite)
        // For the edge (l2), we need intersection within segment
        if (u >= 0 && u <= 1) {
            return new Vec2D(
                x1 + t * (x2 - x1),
                y1 + t * (y2 - y1)
            );
        }

        return null;
    }

    /**
     * Build profile bounds from sketch
     * Calculates min/max Y values for intersection tests
     */
    generate() {
        if (!this.sketch) return;

        // Guard against plain data objects without methods
        if (typeof this.sketch.getHeight === 'function') {
            this.profileHeight = this.sketch.getHeight();
        }
        if (typeof this.sketch.getMinY === 'function') {
            this.profileMinY = this.sketch.getMinY();
        }
        if (typeof this.sketch.getMaxY === 'function') {
            this.profileMaxY = this.sketch.getMaxY();
        }
    }

    /**
     * Get area of the sketch on this plane
     * @returns {number} Area
     */
    getArea() {
        return (this.sketch && typeof this.sketch.getArea === 'function') ? this.sketch.getArea() : 0;
    }

    /**
     * Get center of mass in world coordinates
     * @returns {THREE.Vector3|null} Center of mass
     */
    getCentreOfMass() {
        if (!this.sketch || typeof this.sketch.getCentreOfMass !== 'function') return null;

        const centre2D = this.sketch.getCentreOfMass();
        if (!centre2D) return null;

        return this.getWorldPos(centre2D);
    }

    /**
     * Clone this slice plane
     * @returns {SlicePlane} Cloned plane
     */
    clone() {
        const cloned = new SlicePlane({
            position: this.position.clone(),
            normal: this.normal.clone(),
            thickness: this.thickness,
            guide: this.guide,
            selected: this.selected
        });

        if (this.sketch && typeof this.sketch.clone === 'function') {
            cloned.setSketch(this.sketch.clone());
        }

        return cloned;
    }

    /**
     * Copy this slice plane (similar to clone but preserves ID)
     * @returns {SlicePlane} Copied plane
     */
    copy() {
        const copied = this.clone();
        copied.id = this.id;
        return copied;
    }

    /**
     * Select this plane
     */
    select() {
        this.selected = true;
        if (this.sketch && typeof this.sketch.select === 'function') {
            this.sketch.select();
        }
    }

    /**
     * Unselect this plane
     */
    unselect() {
        this.selected = false;
        if (this.sketch && typeof this.sketch.unselect === 'function') {
            this.sketch.unselect();
        }
    }

    /**
     * Update plane state
     */
    update() {
        // Ensure thickness is positive
        if (this.thickness <= 0) {
            this.thickness = 0.1;
        }

        if (this.sketch && typeof this.sketch.update === 'function') {
            this.sketch.update();
        }

        this.generate();
    }

    /**
     * Scale the plane and sketch
     * @param {number} scale - Scale factor
     * @param {THREE.Vector3} centre - Center point for scaling
     */
    scale(scale, centre) {
        // Scale position relative to centre
        this.position.sub(centre);
        this.position.multiplyScalar(scale);
        this.position.add(centre);

        // Update plane
        this.plane.setFromNormalAndCoplanarPoint(this.normal, this.position);

        // Scale sketch
        if (this.sketch && typeof this.sketch.scale === 'function') {
            this.sketch.scale(scale, this.getPlanePos(centre));
        }
    }

    /**
     * Flip horizontally
     * @param {THREE.Vector3} centre - Center point for flipping
     */
    flipHorizontal(centre) {
        if (this.sketch && typeof this.sketch.flipHorizontal === 'function') {
            const centre2D = this.getPlanePos(centre);
            this.sketch.flipHorizontal(centre2D);
        }
    }

    /**
     * Export to JSON
     * @returns {Object} JSON representation
     */
    toJSON() {
        return {
            id: this.id,
            guide: this.guide,
            thickness: this.thickness,
            position: {
                x: this.position.x,
                y: this.position.y,
                z: this.position.z
            },
            normal: {
                x: this.normal.x,
                y: this.normal.y,
                z: this.normal.z
            },
            sketch: (this.sketch && typeof this.sketch.toJSON === 'function') ? this.sketch.toJSON() : this.sketch
        };
    }

    /**
     * Load from JSON
     * @param {Object} data - JSON data
     * @param {Sketch} sketch - Optional sketch instance
     * @returns {SlicePlane} New SlicePlane instance
     */
    static fromJSON(data, sketch = null) {
        const plane = new SlicePlane({
            id: data.id !== undefined ? data.id : SlicePlane.planeIdCounter++,
            position: new THREE.Vector3(data.position.x, data.position.y, data.position.z),
            normal: new THREE.Vector3(data.normal.x, data.normal.y, data.normal.z),
            thickness: data.thickness || 5.0,
            guide: data.guide || false
        });

        if (sketch) {
            plane.setSketch(sketch);
        } else if (data.sketch) {
            // Would need to construct Sketch from JSON here
            console.warn('Sketch deserialization not yet implemented');
        }

        return plane;
    }

    /**
     * Get string representation
     * @returns {string} Description
     */
    toString() {
        return `SlicePlane[id=${this.id}, pos=(${this.position.x.toFixed(1)}, ${this.position.y.toFixed(1)}, ${this.position.z.toFixed(1)}), normal=(${this.normal.x.toFixed(2)}, ${this.normal.y.toFixed(2)}, ${this.normal.z.toFixed(2)})]`;
    }
}
